import React, { useState, useEffect, useRef } from "react";
import { Box, Drawer, Typography, Divider } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import GaDocument from "./GaDocument";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeSlug from "rehype-slug";
import rehypeStringify from "rehype-stringify";

const DocumentViewer = ({ title, content }) => {
  const [toc, setToc] = useState([]); // State for TOC
  const [selectedItem, setSelectedItem] = useState(null); // State for selected TOC item
  const documentContainerRef = useRef(null);

  useEffect(() => {
    if (documentContainerRef.current) {
      documentContainerRef.current.scrollTop = 0;
    }

    const extractToc = () => (tree) => {
      try {
        if (!tree || !tree.children) {
          console.error("Invalid tree structure:", tree);
          return;
        }

        const toc = [];
        tree.children.forEach((node, index) => {
          if (
            node.tagName === "h1" ||
            node.tagName === "h2" ||
            node.tagName === "h3"
          ) {
            const id =
              node.properties && node.properties.id
                ? node.properties.id
                : `toc-item-${index}`;
            const textNode = node.children.find(
              (child) => child.type === "text"
            );
            if (id && textNode) {
              toc.push({ id, title: textNode.value, tagName: node.tagName });
            }
          }
        });
        setToc(toc); // Ensure TOC is set correctly
      } catch (error) {
        console.error("Error extracting TOC:", error);
      }
    };

    const processContent = async () => {
      try {
        const processor = unified()
          .use(remarkParse) // Parse markdown
          .use(remarkRehype) // Convert to HTML
          .use(rehypeSlug) // Add slugs
          .use(extractToc) // Extract TOC
          .use(rehypeStringify); // Convert to string

        // Process the content through the entire pipeline
        await processor.process(content);
      } catch (error) {
        console.error("Error processing content:", error);
      }
    };

    processContent();
    setSelectedItem(null); // Reset selected item when content changes
  }, [content]);

  const handleTocClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setSelectedItem(id); // Set the selected item
    }
  };

  const renderTree = (items) => {
    return items.map((item, index) => (
      <TreeItem
        key={item.id || `toc-item-${index}`}
        itemId={item.id || `toc-item-${index}`}
        label={item.title}
        onClick={() => handleTocClick(item.id || `toc-item-${index}`)}
        sx={{
          cursor: "pointer",
          "& .MuiTreeItem-content .MuiTreeItem-iconContainer": {
            display: "none ", // Hide the icon container
          },
          "& .MuiTreeItem-content .MuiTreeItem-label": {
            fontWeight:
              item.tagName === "h1"
                ? "bold"
                : item.tagName === "h2"
                ? "medium"
                : "normal",
            fontSize: "0.875rem",
          },
          "& .MuiTreeItem-label": {
            fontWeight:
              item.tagName === "h1"
                ? "bold"
                : item.tagName === "h2"
                ? "medium"
                : "normal",
            fontSize: "0.875rem",
            marginLeft:
              item.tagName === "h2" ? 2 : item.tagName === "h3" ? 4 : 0, // Indent based on heading level
            color: selectedItem === (item.id || `toc-item-${index}`) ? 'primary.main' : 'inherit', // Change color if selected
          },
          "& .Mui-selected .MuiTreeItem-label": {
            color: 'primary.main', // Ensure the selected color is applied
            marginLeft:
              item.tagName === "h2" ? 2 : item.tagName === "h3" ? 4 : 0, // Ensure margin is applied for selected items
          },
          marginBottom: 0, // Reduce space between items
        }}
        selected={selectedItem === (item.id || `toc-item-${index}`)} // Ensure the selected state is applied
      >
        {item.children && renderTree(item.children)}
      </TreeItem>
    ));
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1 }}>
      <Box
        component="section"
        sx={{ flexGrow: 1, overflow: "auto", padding: 2 }}
        ref={documentContainerRef}
      >
        {/* <Typography
          variant="h4"
          sx={{ mb: 6, mt: 2, textAlign: "center", fontWeight: "bold" }}
        >
          {title}
        </Typography> */}
        <GaDocument content={content} /> {/* Pass the markdown text */}
      </Box>
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          width: 300, // Make the TOC area wider
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 300,
            boxSizing: "border-box",
            position: "relative",
            backgroundColor: "transparent", // Match background color with main document
            border: "none", // Remove border
          },
        }}
      >
        <Box sx={{ overflow: "auto", m: 2, padding: 0 }}>
          <Typography
            variant="body1"
            sx={{ mt: 2, mb: 1, fontWeight: "bold", textAlign: "left" }}
          >
            Table of Contents
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <SimpleTreeView key={content}> {/* Use content as key to force re-render */}
            {renderTree(toc)}
          </SimpleTreeView>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DocumentViewer;
